import mqtt from 'mqtt';
import notificationSound from '../assets/audio/notification.mp3';

const notificationAudio = new Audio(notificationSound);

let isAudioUnlocked = false;

class MqttService {
  constructor() {
    this.client = null;
    this.subscribers = [];

    this.reconnectInterval = 5000;
    this.maxReconnectInterval = 60000;

    this.reconnectTimer = null;
    this.shouldReconnect = true;

    this.userId = null;
  }

  unlockAudio() {
    if (isAudioUnlocked) return;

    notificationAudio
      .play()
      .then(() => {
        notificationAudio.pause();
        notificationAudio.currentTime = 0;
        isAudioUnlocked = true;
      })
      .catch(() => {
        // Browser may require user interaction first
      });
  }

  connect(userId) {
    if (!userId) return;

    this.shouldReconnect = true;
    this.userId = userId;

    if (
      this.client &&
      (this.client.connecting || this.client.connected)
    ) {
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    const isSecurePage =
      window.location.protocol === 'https:';

    const isLocalhost = [
      'localhost',
      '127.0.0.1',
    ].includes(window.location.hostname);

    let connectUrl;

    if (!isSecurePage && isLocalhost) {
      const host =
        process.env.REACT_APP_MQTT_HOST ||
        window.location.hostname;

      const port =
        process.env.REACT_APP_MQTT_WS_PORT ||
        9002;

      connectUrl = `ws://${host}:${port}`;
    } else {
      const protocol = isSecurePage
        ? 'wss'
        : 'ws';

      connectUrl =
        `${protocol}://${window.location.host}/mqtt-ws`;
    }

    console.log(
      'Connecting to MQTT:',
      connectUrl
    );

    if (this.client) {
      // Do NOT removeAllListeners() here: mqtt.js registers its own
      // internal 'close' listener (which clears the pending connack
      // timeout timer) inside the client constructor. Stripping every
      // listener before end() leaves that timer running on the discarded
      // client, so it fires ~connectTimeout later and emits an 'error'
      // with no listener attached (also removed), crashing as an
      // uncaught "connack timeout" exception. The 'close' handler below
      // guards against acting on a stale client via an identity check
      // instead, so mqtt.js's own cleanup listeners can stay intact.
      try {
        this.client.end(true);
      } catch (error) {
        console.warn(
          'Error closing old MQTT client:',
          error
        );
      }

      this.client = null;
    }

    const client = mqtt.connect(connectUrl, {
      reconnectPeriod: 0,

      connectTimeout: 15000,

      protocolVersion: 4,

      clean: true,

      keepalive: 30,

      clientId:
        `solyntek_web_${userId}_${Math.random()
          .toString(16)
          .slice(2)}`,
    });

    this.client = client;

    client.on('connect', () => {
      console.log('MQTT connected');

      this.reconnectInterval = 5000;

      const topic =
        `solyntek/user/${this.userId}/pip_notification`;

      client.subscribe(
        topic,
        { qos: 0 },
        (err) => {
          if (err) {
            console.error(
              `Failed to subscribe to MQTT topic: ${topic}`,
              err
            );
            return;
          }

          console.log(
            'MQTT subscribed:',
            topic
          );
        }
      );
    });

    client.on(
      'message',
      (topic, message) => {
        if (
          localStorage.getItem(
            'solyntek_alerts_muted'
          ) !== '1'
        ) {
          notificationAudio
            .play()
            .catch(() => {
              // User hasn't unlocked audio yet
            });
        }

        try {
          const payload =
            JSON.parse(
              message.toString()
            );

          this.notifySubscribers(
            payload
          );
        } catch (error) {
          console.error(
            'Failed to parse MQTT message:',
            error
          );
        }
      }
    );

    client.on(
      'error',
      (err) => {
        console.error(
          'MQTT error:',
          err
        );
      }
    );

    client.on(
      'offline',
      () => {
        console.warn(
          'MQTT offline'
        );
      }
    );

    client.on(
      'close',
      () => {
        console.warn(
          'MQTT connection closed'
        );

        if (
          this.client !== client
        ) {
          // This client was already superseded or torn down
          // (see the teardown comment above connect()); ignore
          // its close event so we don't schedule a duplicate
          // reconnect on top of the current client.
          return;
        }

        if (
          !this.shouldReconnect
        ) {
          return;
        }

        if (
          this.reconnectTimer
        ) {
          return;
        }

        const jitter =
          Math.floor(
            Math.random() * 2000
          );

        const delay =
          this.reconnectInterval +
          jitter;

        this.reconnectInterval =
          Math.min(
            this.reconnectInterval *
              2,
            this.maxReconnectInterval
          );

        console.log(
          `MQTT reconnect in ${delay}ms`
        );

        this.reconnectTimer =
          setTimeout(() => {
            this.reconnectTimer =
              null;

            this.connect(
              this.userId
            );
          }, delay);
      }
    );
  }

  isConnected() {
    return !!this.client
      ?.connected;
  }

  subscribe(callback) {
    if (
      typeof callback !==
      'function'
    ) {
      return () => {};
    }

    this.subscribers.push(
      callback
    );

    return () =>
      this.unsubscribe(
        callback
      );
  }

  unsubscribe(callback) {
    this.subscribers =
      this.subscribers.filter(
        (sub) =>
          sub !== callback
      );
  }

  notifySubscribers(payload) {
    this.subscribers.forEach(
      (callback) => {
        try {
          callback(payload);
        } catch (error) {
          console.error(
            'Error in MQTT subscriber:',
            error
          );
        }
      }
    );
  }

  close() {
    this.shouldReconnect =
      false;

    if (
      this.reconnectTimer
    ) {
      clearTimeout(
        this.reconnectTimer
      );

      this.reconnectTimer =
        null;
    }

    if (this.client) {
      // See the teardown comment in connect(): leave mqtt.js's own
      // internal listeners in place so it can clear its pending
      // connack timeout timer. shouldReconnect is already false above,
      // so our own 'close' handler will no-op on this client's close
      // event.
      try {
        this.client.end(
          true
        );
      } catch (error) {
        console.warn(
          'Error closing MQTT client:',
          error
        );
      }

      this.client = null;
    }

    this.reconnectInterval =
      5000;
  }
}

const mqttService =
  new MqttService();

export default mqttService;